import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./leaderboard.css";

function Leaderboard() {
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);

  const [top5, setTop5] = useState([]);
  const [currentUserEntry, setCurrentUserEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const sessionId = storedUser.sessionId ?? user?.sessionId;
  const organizationId = storedUser.organizationId ?? user?.organizationId;
  const userId = String(
    storedUser.userId ?? storedUser.id ?? user?.userId ?? user?.id ?? ""
  );

  useEffect(() => {
    if (!adminToken || !sessionId || !organizationId) {
      setLoading(false);
      if (!sessionId || !organizationId) {
        setError("Session not found. Please log in again.");
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/getLeaderboard`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            sessionId,
            organizationId,
            userId: userId || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success) {
          setTop5(Array.isArray(data.data) ? data.data : []);
          setCurrentUserEntry(data.currentUserEntry ?? null);
        } else {
          setError(data.error || data.message || "Failed to load leaderboard");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adminToken, sessionId, organizationId, userId]);

  /* ── Merge top5 + currentUserEntry ── */
  const rowsToRender = useMemo(() => {
    const rows = top5.map((r) => ({
      ...r,
      isCurrentUser: userId && r.userId === userId,
    }));
    if (
      currentUserEntry &&
      !top5.some((r) => r.userId === currentUserEntry.userId)
    ) {
      rows.push({ ...currentUserEntry, isCurrentUser: true, isExtraRow: true });
    }
    return rows;
  }, [top5, currentUserEntry, userId]);

  /* ── Rank medal emoji ── */
  const rankDisplay = (rank) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return rank;
  };

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-container">
        <h2 className="leaderboard-title">Leaderboard</h2>

        {loading ? (
          <div className="leaderboard-loading">Loading…</div>
        ) : error ? (
          <div className="leaderboard-error">{error}</div>
        ) : (
          <div className="leaderboard-table-wrap">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Name</th>
                  <th>Score</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {rowsToRender.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="leaderboard-empty">
                      No completed games yet.
                    </td>
                  </tr>
                ) : (
                  rowsToRender.map((row) => (
                    <tr
                      key={`${row.rank}-${row.userId}-${
                        row.isExtraRow ? "me" : "top"
                      }`}
                      className={[
                        row.isCurrentUser ? "leaderboard-row--you" : "",
                        row.isExtraRow ? "leaderboard-row--extra" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <td>{rankDisplay(row.rank)}</td>
                      <td>
                        {row.name}
                        {row.isCurrentUser ? (
                          <span className="leaderboard-you-badge">You</span>
                        ) : null}
                      </td>
                      <td>{row.score}</td>
                      <td>{row.time || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;
